import type { ReactNode } from 'react';
import { CircleAlert, RotateCw } from 'lucide-react';
import { isApiError } from '@kidova/api-client';
import { Button, Card, Skeleton, SkeletonText } from '@kidova/ui-web';
import { useI18n, type AppI18n } from '../i18n';

export type ErrorI18n = Pick<AppI18n, 'locale' | 't' | 'errorCode'>;

/**
 * Server messages are English only, so they are shown as written in English and replaced by the
 * catalog text for their code in every other language. Client-side codes are never shown raw.
 */
export function errorMessage(error: unknown, i18n: ErrorI18n): string {
  if (!isApiError(error)) return i18n.t('error.title');
  if (error.code === 'NETWORK') return i18n.t('error.network');
  if (i18n.locale === 'en' && error.code !== 'UNEXPECTED' && error.code !== 'INTERNAL_ERROR')
    return error.message;
  return i18n.errorCode(error.code);
}

/** Field → message for a 422; outside English each field gets the localized validation text. */
export function fieldErrorMessages(error: unknown, i18n: ErrorI18n): Record<string, string> {
  if (!isApiError(error)) return {};
  const fields = error.fieldErrors();
  if (i18n.locale === 'en') return fields;
  const text = i18n.errorCode(error.code);
  return Object.fromEntries(Object.keys(fields).map((f) => [f, text]));
}

/** §23.4 errors: the envelope's message, the request ID for support, and a retry. */
export function ErrorState({
  error,
  onRetry,
  compact,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const message = errorMessage(error, i18n);
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <div
      role="alert"
      className={`flex flex-col items-start gap-3 ${compact ? 'p-4' : 'surface p-6'}`}
      data-testid="error-state"
    >
      <div className="flex items-center gap-2 text-text-unprotected">
        <CircleAlert size={20} aria-hidden />
        <p className="text-body font-semibold">{t('error.title')}</p>
      </div>
      <p className="text-body text-text-primary">{message}</p>
      {requestId && (
        <p className="text-caption text-text-secondary num" data-testid="request-id">
          {t('error.requestId', { id: requestId })}
        </p>
      )}
      {onRetry && (
        <Button variant="secondary" size="sm" iconStart={RotateCw} onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

/** First-load placeholder for a page body; the shell around it is already final (no layout jump). */
export function PageSkeleton({ cards = 3 }: { cards?: number }) {
  return (
    <div aria-busy className="flex flex-col gap-6" data-testid="page-skeleton">
      <div className="flex flex-col gap-2">
        <Skeleton width={220} height={32} />
        <Skeleton width={320} height={18} />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: cards }, (_, i) => (
          <Card key={i} padding="md">
            <SkeletonText lines={4} />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  leading,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  leading?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 items-center gap-4">
        {leading}
        <div className="min-w-0">
          <h1 className="text-h1 text-text-primary">{title}</h1>
          {subtitle && <p className="mt-1 text-body text-text-secondary">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Section({
  title,
  description,
  action,
  children,
  id,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section className="surface p-5 md:p-6" aria-labelledby={id}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={id} className="text-h3 text-text-primary">
            {title}
          </h2>
          {description && <p className="mt-1 text-small text-text-secondary">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
